/*
 * ONELLE behaviours
 * ---------------------------------------------------------------------------
 * Plain module, no dependencies on Horizon's component base class, so it can
 * run early and survive section re-renders.
 */

import {
  getScrollTop,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const SCROLL_THRESHOLD = 80;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ===========================================================================
 * Block 3 — Header scroll state
 *
 * Horizon already flips `data-sticky-state` the moment the header leaves the
 * top of the page. ONELLE needs a deliberate 80px threshold and a separate
 * hide-on-down / show-on-up state, so both live on their own attributes:
 *
 *   data-onelle-scrolled  past 80px — header paints bone, text goes ink
 *   data-onelle-hidden    scrolling down past 80px — header lifts out on Y
 *
 * Nothing here toggles `display`, so the header stays focusable and keeps its
 * place in the tab order while it is translated off-screen.
 *
 * Scroll position comes from @theme/scroll-container, not from window: above
 * 990px Horizon scrolls `.page-wrapper` rather than the document, so
 * window.scrollY is pinned at 0 there.
 * ======================================================================== */
function initHeaderScroll() {
  const header = document.querySelector('#header-component');
  if (!header) return;

  let lastY = getScrollTop();
  let ticking = false;
  let scrollTarget = getScrollEventTarget();

  const update = () => {
    ticking = false;
    const y = Math.max(0, getScrollTop());
    const past = y > SCROLL_THRESHOLD;

    header.dataset.onelleScrolled = past ? 'true' : 'false';

    // Never hide above the threshold, and never hide while a menu is open —
    // the drawer and the mega menu both anchor to the header.
    const menuOpen = header.querySelector('[aria-expanded="true"]') != null;
    const scrollingDown = y > lastY;

    if (!past || menuOpen) {
      header.dataset.onelleHidden = 'false';
    } else if (Math.abs(y - lastY) > 4) {
      header.dataset.onelleHidden = scrollingDown ? 'true' : 'false';
    }

    lastY = y;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  update();
  scrollTarget.addEventListener('scroll', onScroll, { passive: true });

  // The scroll container swaps at 990px, so the listener has to move with it.
  scrollContainerMediaQuery.addEventListener('change', () => {
    scrollTarget.removeEventListener('scroll', onScroll);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    lastY = getScrollTop();
    update();
  });

  // A focus landing inside the header always brings it back into view.
  header.addEventListener('focusin', () => {
    header.dataset.onelleHidden = 'false';
  });
}

/* ===========================================================================
 * Block 4 — Product image fade-in
 *
 * Cards hold their frame with a --line placeholder and fade the photograph in
 * once it decodes, so nothing jumps as the grid fills. CSS alone can't tell a
 * decoded image from a pending one, so the state is marked here.
 *
 * The `load` event doesn't bubble, but it does capture — one listener on the
 * document therefore also covers images added later by quick-add, load-more or
 * a section re-render, with no observer to maintain.
 * ======================================================================== */
function initImageFade() {
  const mark = (img) => {
    if (img instanceof HTMLImageElement) img.dataset.onelleLoaded = 'true';
  };

  document.addEventListener(
    'load',
    (event) => {
      mark(event.target);
    },
    true
  );

  // Images already decoded before this ran — cached ones, mostly — never fire
  // another load event, so sweep them once.
  const sweep = () => {
    for (const img of document.images) {
      if (img.complete) mark(img);
    }
  };

  sweep();
  window.addEventListener('pageshow', sweep);
}

/* ===========================================================================
 * Block 5 — Scroll reveal
 *
 * Sections rise 24px and fade in once 15% of them is on screen, staggered
 * 60ms apart inside a grid and capped at 8 steps so a long grid never leaves
 * the last card waiting. Each element reveals once and is then released.
 *
 * Under prefers-reduced-motion nothing is observed and nothing is marked, so
 * the CSS default of "already visible" stands. That is a hard requirement,
 * not a preference — see the reduced-motion block in assets/onelle.css.
 * ======================================================================== */
const REVEAL_STAGGER_MS = 60;
const REVEAL_MAX_STEPS = 8;

/* Section-level containers worth revealing, plus the grids whose children
   stagger. Kept as a list so it reads as a design decision, not a regex. */
const REVEAL_TARGETS = [
  '.shopify-section > .section',
  '.shopify-section > .section-wrapper',
  '.onelle-reveal',
];

const STAGGER_PARENTS = ['.product-grid', '.onelle-stagger'];

function initScrollReveal() {
  if (reducedMotion.matches) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.dataset.onelleRevealed = 'true';
        observer.unobserve(entry.target); // once only
      }
    },
    { threshold: 0.15 }
  );

  const observe = (element, index) => {
    if (element.dataset.onelleReveal) return;
    element.dataset.onelleReveal = '';
    if (index != null) {
      // Written inline rather than as a custom property the stylesheet reads:
      // grid items carry their own `transition` shorthand from Horizon, which
      // resets transition-delay at equal specificity later in the cascade.
      const step = Math.min(index, REVEAL_MAX_STEPS);
      element.style.transitionDelay = `${step * REVEAL_STAGGER_MS}ms`;
    }
    observer.observe(element);
  };

  for (const selector of REVEAL_TARGETS) {
    for (const element of document.querySelectorAll(selector)) observe(element, null);
  }

  // Inside a grid the children carry the stagger, so the grid itself steps
  // aside and lets each card announce itself.
  for (const selector of STAGGER_PARENTS) {
    for (const grid of document.querySelectorAll(selector)) {
      grid.dataset.onelleReveal = 'skip';
      [...grid.children].forEach((child, index) => observe(child, index));
    }
  }
}

/* ===========================================================================
 * Block 5 — Hero parallax
 *
 * A very slow drift, capped at 8% of the hero's height, and only above the
 * mobile breakpoint. Reduced motion switches it off entirely.
 * ======================================================================== */
const PARALLAX_MAX_RATIO = 0.08;
const parallaxAllowed = window.matchMedia('(min-width: 750px)');

function initHeroParallax() {
  if (reducedMotion.matches) return;

  const hero = document.querySelector('.shopify-section:first-child [class*="hero"], .onelle-parallax');
  const media = hero?.querySelector('img, video, .background-media, svg');
  if (!media) return;

  let ticking = false;

  const update = () => {
    ticking = false;
    if (!parallaxAllowed.matches) {
      media.style.removeProperty('--onelle-parallax-y');
      return;
    }
    const rect = hero.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;

    // 0 at the top of the hero, 1 once it has fully scrolled past.
    const progress = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height)));
    media.style.setProperty('--onelle-parallax-y', `${(progress * PARALLAX_MAX_RATIO * rect.height).toFixed(2)}px`);
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  media.dataset.onelleParallax = 'true';
  update();
  getScrollEventTarget().addEventListener('scroll', onScroll, { passive: true });
  parallaxAllowed.addEventListener('change', update);
}

/* ------------------------------------------------------------------------ */

function init() {
  initHeaderScroll();
  initImageFade();
  initScrollReveal();
  initHeroParallax();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export { reducedMotion, SCROLL_THRESHOLD };
